/**
 * @module deploy
 * @description Public deployment-platform adapter contract surface. Runtime
 * deployment implementations live in provider slices; this subpath exports only
 * the interface, Zod schemas, and thin error class.
 */

import type {
  AdapterHealth as SharedAdapterHealth,
  DeployAdapter as SharedDeployAdapter,
  DeploymentResult as SharedDeploymentResult,
  DeploymentTarget as SharedDeploymentTarget,
} from "@geenius/adapters";
import {
  AdapterHealthSchema as SharedAdapterHealthSchema,
  DeployContractError as SharedDeployContractError,
  DeploymentResultSchema as SharedDeploymentResultSchema,
  DeploymentTargetSchema as SharedDeploymentTargetSchema,
} from "@geenius/adapters";

/**
 * Health result returned by deployment providers after checking platform
 * connectivity and deployment API availability.
 */
export type AdapterHealth = SharedAdapterHealth;

/**
 * SDK-free deployment adapter contract implemented by downstream provider
 * packages and app-owned deployment modules.
 */
export type DeployAdapter = SharedDeployAdapter;

/**
 * Serializable deployment result returned by deploy, rollback, lookup, and
 * list operations.
 */
export type DeploymentResult = SharedDeploymentResult;

/**
 * Provider-neutral deployment target accepted by deployment-platform adapters.
 */
export type DeploymentTarget = SharedDeploymentTarget;

/**
 * Contract-layer error for invalid deployment payloads or provider-boundary
 * violations before a concrete platform SDK receives the request.
 */
export const DeployContractError = SharedDeployContractError;

/**
 * Runtime validator for adapter health records returned by providers.
 */
export const AdapterHealthSchema = SharedAdapterHealthSchema;

/**
 * Runtime validator for deployment result records returned by providers.
 */
export const DeploymentResultSchema = SharedDeploymentResultSchema;

/**
 * Runtime validator for provider-neutral deployment target configuration.
 */
export const DeploymentTargetSchema = SharedDeploymentTargetSchema;

export type {
  DeployProvider,
  DeployProviderManifest,
  ProviderDeployAdapter,
  ProviderDeployAdapterInput,
  ProviderDeploymentTarget,
} from "./provider-slice";

export {
  createProviderDeployAdapter,
  isProviderDeploymentTarget,
  validateProviderAdapterHealth,
  validateProviderDeploymentResult,
  validateProviderDeploymentTarget,
} from "./provider-slice";
